import React, { useState } from "react";
import { auth, googleProvider } from "../../config/firebase.js";
import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import GoogleAnalytics from '../../analyticsComponents/GoogleAnalytics.js';
import logger from '../../utils/logger';

const SignInPage = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loginAttempts, setLoginAttempts] = useState(0);
    const [showPopup, setShowPopup] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [passwordVisible, setPasswordVisible] = useState(false);
    const navigate = useNavigate();
    const db = getFirestore();

    const handleEmailSignIn = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrorMessage("");

        try {
            await signInWithEmailAndPassword(auth, email.trim(), password.trim());
            setEmail("");
            setPassword("");
            setLoginAttempts(0);
            navigate("/");
        } catch (err) {
            logger.error("Email sign-in error:", err);
            switch (err.code) {
                case "auth/invalid-credential":
                case "auth/wrong-password":
                case "auth/user-not-found":
                    setErrorMessage("Invalid email or password. Please try again.");
                    break;
                case "auth/invalid-email":
                    setErrorMessage("Please enter a valid email address.");
                    break;
                case "auth/user-disabled":
                    setErrorMessage("This account has been disabled. Please contact support.");
                    break;
                case "auth/too-many-requests":
                    setErrorMessage("Too many failed sign-in attempts. Please try again later.");
                    break;
                default:
                    setErrorMessage("An error occurred. Please try again.");
                    logger.error("Unhandled auth error code:", err.code);
            }

            setLoginAttempts((prev) => prev + 1);
            if (loginAttempts >= 2) {
                setShowPopup(true);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setIsSubmitting(true);
        setErrorMessage("");

        try {
            const result = await signInWithPopup(auth, googleProvider);
            const user = result.user;

            const userDocRef = doc(db, "users", user.uid);
            const userDoc = await getDoc(userDocRef);

            if (!userDoc.exists()) {
                await setDoc(userDocRef, {
                    full_name: user.displayName || null,
                    email: user.email,
                    role: "open access user",
                    requested_role: null,
                });
            }

            setLoginAttempts(0);
            navigate("/");
        } catch (err) {
            logger.error("Google sign-in error:", err);
            switch (err.code) {
                case "auth/popup-closed-by-user":
                    setErrorMessage("Sign-in process was closed. Please try again.");
                    break;
                case "auth/cancelled-popup-request":
                    setErrorMessage("Sign-in process was canceled. Please try again.");
                    break;
                case "auth/network-request-failed":
                    setErrorMessage("Network error. Please check your connection.");
                    break;
                case "auth/account-exists-with-different-credential":
                    setErrorMessage("An account already exists with this email using a different sign-in method.");
                    break;
                case "auth/invalid-credential":
                    setErrorMessage("Invalid credentials. Please try again.");
                    break;
                case "auth/user-disabled":
                    setErrorMessage("This account has been disabled. Please contact support.");
                    break;
                case "auth/too-many-requests":
                    setErrorMessage("Too many attempts. Please try again later.");
                    break;
                default:
                    setErrorMessage("An error occurred during Google sign-in. Please try again.");
                    logger.error("Unhandled Google auth error code:", err.code);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const closePopup = () => setShowPopup(false);

    return (
        <div className="min-h-screen w-full bg-gray-50">
            <GoogleAnalytics page="SignInPage" eventType="signin_page" />

            {/* Hero banner */}
            <div className="relative bg-gray-900 overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-brand-500 blur-3xl" />
                    <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-brand-400 blur-3xl" />
                </div>
                <div className="relative z-10 max-w-6xl mx-auto px-4 py-12 md:py-16 text-center">
                    <span className="inline-block px-2.5 py-0.5 rounded-full bg-brand-600/20 text-brand-300 text-xs font-semibold mb-3">Account</span>
                    <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">Welcome back</h1>
                    <p className="mt-2 text-gray-400 text-sm md:text-base max-w-md mx-auto">Sign in to access your resources, contribute materials, and rate content.</p>
                </div>
            </div>

            {/* Card */}
            <div className="max-w-md mx-auto px-4 -mt-6 relative z-20 pb-16">
                <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-6 md:p-8">

                    {/* Error message */}
                    {errorMessage && (
                        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 mb-5">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-red-700 text-sm">{errorMessage}</p>
                        </div>
                    )}

                    <form onSubmit={handleEmailSignIn} className="space-y-4">
                        {/* Email */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <div className="relative">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <input
                                    id="email"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition"
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                            <div className="relative">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <input
                                    id="password"
                                    type={passwordVisible ? "text" : "password"}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition"
                                />
                                <button
                                    type="button"
                                    onMouseDown={() => setPasswordVisible(true)}
                                    onMouseUp={() => setPasswordVisible(false)}
                                    onMouseLeave={() => setPasswordVisible(false)}
                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                                    aria-label="Show password"
                                >
                                    {passwordVisible ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Forgot password */}
                        <div className="text-right">
                            <Link to="/forgot-password" className="text-sm text-brand-600 hover:text-brand-700 hover:underline font-medium">
                                Forgot your password?
                            </Link>
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`w-full py-2.5 rounded-lg text-white font-semibold text-sm transition-all flex items-center justify-center gap-2 shadow-sm ${
                                isSubmitting
                                    ? "bg-brand-400 cursor-not-allowed"
                                    : "bg-brand-600 hover:bg-brand-700 hover:shadow-md active:scale-[0.98]"
                            }`}
                        >
                            {isSubmitting && (
                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                </svg>
                            )}
                            {isSubmitting ? "Signing in…" : "Sign in"}
                        </button>
                    </form>

                    {/* Divider */}
                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-200" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="bg-white px-3 text-gray-400">or continue with</span>
                        </div>
                    </div>

                    {/* Google */}
                    <button
                        onClick={handleGoogleSignIn}
                        disabled={isSubmitting}
                        className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 border ${
                            isSubmitting
                                ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm active:scale-[0.98]"
                        }`}
                    >
                        {isSubmitting ? (
                            <svg className="animate-spin h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                            </svg>
                        ) : (
                            <svg className="h-5 w-5" viewBox="0 0 24 24">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 001 12c0 1.97.47 3.83 1.18 5.47l3.66-2.84z" fill="#FBBC05"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                        )}
                        {isSubmitting ? "Signing in…" : "Sign in with Google"}
                    </button>

                    {/* Sign up link */}
                    <p className="text-gray-500 mt-6 text-center text-sm">
                        Don't have an account?{" "}
                        <Link to="/sign-up" className="text-brand-600 font-semibold hover:text-brand-700 hover:underline">
                            Sign up
                        </Link>
                    </p>
                </div>
            </div>

            {/* Too-many-attempts popup */}
            {showPopup && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
                    <div className="bg-white p-6 rounded-2xl shadow-lg max-w-sm w-full text-center border border-gray-100">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-50 text-red-500 mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Too many attempts</h3>
                        <p className="text-gray-500 text-sm">Your account may be temporarily disabled for security reasons. Please try again later or reset your password.</p>
                        <div className="mt-5 flex flex-col gap-2">
                            <Link
                                to="/forgot-password"
                                onClick={closePopup}
                                className="w-full py-2 rounded-lg bg-brand-600 text-white font-semibold text-sm hover:bg-brand-700 transition-all"
                            >
                                Reset password
                            </Link>
                            <button
                                onClick={closePopup}
                                className="w-full py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-all"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SignInPage;
